import { apiClient, handleApiRequest } from './client'

export interface TimetableEntry {
  id: string
  school_id?: string
  teacher_id: string
  teacher_name?: string
  teacher_first_name?: string
  teacher_last_name?: string
  teacher_code?: string
  time_slot_id?: string
  time_slot_name?: string
  start_time?: string
  end_time?: string
  room_id?: string
  room_name?: string
  klass_id?: string
  klass_name?: string
  subject_id?: string
  subject_name?: string
  subject_token?: string | null
  subject_unclear?: boolean
  background_id?: string
  background_name?: string
  date: string
  day_of_week?: number
  lesson_number?: number | null
  is_recurring: boolean
  recurrence_pattern?: string
  is_manual_entry: boolean
  cancelled: boolean
  cancelled_reason?: string
  valid_from?: string | null
  valid_to?: string | null
  substitution_excluded?: boolean
}

export interface TimetableListResponse {
  items: TimetableEntry[]
  pagination: { page: number; page_size: number; total_items: number; total_pages: number }
}

export interface TimetableCreateRequest {
  teacher_id: string
  time_slot_id?: string
  time_slot_label?: string
  room_id?: string
  klass_id?: string
  subject_id?: string
  background_id?: string
  subject_name?: string
  room_name?: string
  klass_name?: string
  lesson_number?: number
  lesson_type?: string
  date?: string
  day_of_week?: number
  is_recurring?: boolean
  recurrence_pattern?: string
  recurrence_end_date?: string
  week_offset?: number
  manual_entry_reason?: string
  valid_from?: string | null
  valid_to?: string | null
}

export interface TimetableUpdateRequest {
  time_slot_id?: string
  time_slot_label?: string
  subject_name?: string
  room_name?: string
  klass_name?: string
  lesson_number?: number
  date?: string | null
  day_of_week?: number
  is_recurring?: boolean
}

export interface BulkCreateRequest {
  teacher_id: string
  time_slot_id: string
  room_id?: string
  klass_id?: string
  subject_id?: string
  background_id?: string
  subject_name?: string
  lesson_type?: string
  start_date: string
  end_date: string
  day_of_week: number
  recurrence_pattern?: string
  skip_holidays?: boolean
  manual_entry_reason?: string
}

export interface ConflictCheckRequest {
  teacher_id: string
  time_slot_id: string
  date: string
  room_id?: string
  klass_id?: string
}

export interface ConflictCheckResult {
  has_conflicts: boolean
  conflicts: Array<{ conflict_type: string; entry_id: string; description: string }>
  conflict_count: number
}

export interface TimetableSummary {
  teacher_id: string
  period: { week_start: string; week_count: number; week_end: string }
  total_entries: number
  entries_by_day: Record<string, { count: number; slots: string[]; subjects: string[]; rooms: string[]; classes: string[] }>
  entries_by_subject: Record<string, number>
  entries_by_class: Record<string, number>
}

export interface CalendarDayData {
  lessons: Array<{
    id: string
    time_slot_name?: string
    subject_name?: string
    room_name?: string
    klass_name?: string
    is_recurring: boolean
    is_manual_entry: boolean
  }>
  open_requests: number
  accepted_substitutions: number
  cancelled: number
}

export interface CalendarView {
  teacher_id: string
  period: { week_start: string; week_count: number; week_end: string }
  days: Record<string, CalendarDayData>
  summary: { total_lessons: number; lessons_with_substitutions: number; open_requests: number }
}

export type RebindCategory = 'subject' | 'room' | 'klass'

export interface SubjectRebindSubject {
  id: string
  name: string
  code?: string | null
  prefix_match: boolean
}

export interface SubjectRebindSibling {
  id: string
  day_of_week: number | null
  time_label: string
  class_name: string | null
}

export interface SubjectRebindField {
  token: string | null
  id: string | null
}

export interface SubjectRebindPreview {
  entry_id: string
  token: string | null
  subject_id: string | null
  class_name: string | null
  day_of_week: number | null
  time_label: string
  allow_alias: boolean
  allow_recategorize?: boolean
  unbounded_validity?: boolean
  siblings: SubjectRebindSibling[]
  subjects: SubjectRebindSubject[]
  rooms?: SubjectRebindSubject[]
  klasses?: SubjectRebindSubject[]
  fields?: Record<RebindCategory, SubjectRebindField>
  field_siblings?: Record<RebindCategory, SubjectRebindSibling[]>
}

export interface SubjectRebindRequest {
  subject_id?: string
  target_id?: string
  source_category?: RebindCategory
  target_category?: RebindCategory
  include_siblings?: boolean
  create_alias?: boolean
  create_target?: boolean
  create_name?: string
}

export interface SubjectRebindUndo {
  entries: Array<{
    id: string
    subject_id: string | null
    subject_name?: string | null
    subject_ids?: string[] | null
    room_id?: string | null
    room_name?: string | null
    klass_id?: string | null
    klass_name?: string | null
  }>
  alias_id: string | null
}

export interface SubjectRebindResult {
  updated_ids: string[]
  subject_id: string | null
  subject_name: string | null
  token: string | null
  source_category?: RebindCategory
  target_category?: RebindCategory
  target_id?: string
  target_name?: string
  alias_created: boolean
  alias_id: string | null
  undo?: SubjectRebindUndo
}

export interface AdminPlanRights {
  load: boolean
  delete: boolean
  edit: boolean
}

export const timetableApi = {
  adminRights: () =>
    handleApiRequest<AdminPlanRights>(apiClient.get('/api/v1/timetables/admin-rights')),

  list: (params?: Record<string, string | number | boolean>) =>
    handleApiRequest<TimetableListResponse>(apiClient.get('/api/v1/timetables', { params })),

  get: (entryId: string) =>
    handleApiRequest<TimetableEntry>(apiClient.get(`/api/v1/timetables/${entryId}`)),

  create: (data: TimetableCreateRequest) =>
    handleApiRequest(apiClient.post('/api/v1/timetables', data)),

  update: (entryId: string, data: TimetableUpdateRequest) =>
    handleApiRequest(apiClient.put(`/api/v1/timetables/${entryId}`, data)),

  listSlots: () =>
    handleApiRequest<Array<{ id: string; slot_number: number | null; start_time: string | null; end_time: string | null }>>(
      apiClient.get('/api/v1/timetables/slots'),
    ),

  extendSpan: (entryId: string, direction: 'up' | 'down') =>
    handleApiRequest<{
      entry_id: string
      neighbor_id: string
      direction: 'up' | 'down'
      created: boolean
      undo_id: string | null
    }>(apiClient.post(`/api/v1/timetables/${entryId}/extend`, { direction })),

  undoExtend: (undoId: string) =>
    handleApiRequest<{ undo_id: string; restored: number; deleted_id: string | null }>(
      apiClient.post('/api/v1/timetables/extend-undo', { undo_id: undoId }),
    ),

  previewSubjectRebind: (entryId: string) =>
    handleApiRequest<SubjectRebindPreview>(apiClient.get(`/api/v1/timetables/${entryId}/subject-rebind`)),

  rebindSubject: (entryId: string, data: SubjectRebindRequest) =>
    handleApiRequest<SubjectRebindResult>(apiClient.post(`/api/v1/timetables/${entryId}/subject-rebind`, data)),

  undoSubjectRebind: (data: SubjectRebindUndo) =>
    handleApiRequest<{ restored_ids: string[]; alias_removed: boolean }>(
      apiClient.post('/api/v1/timetables/subject-rebind/undo', data),
    ),

  patch: (entryId: string, data: Record<string, unknown>) =>
    handleApiRequest(apiClient.patch(`/api/v1/timetables/${entryId}`, data)),

  delete: (entryId: string, params?: Record<string, boolean>) =>
    handleApiRequest(apiClient.delete(`/api/v1/timetables/${entryId}`, { params })),

  cancel: (entryId: string, reason: string, cancelSeries = false) =>
    handleApiRequest(apiClient.post(`/api/v1/timetables/${entryId}/cancel`, { reason, cancel_series: cancelSeries })),

  restore: (entryId: string) =>
    handleApiRequest(apiClient.post(`/api/v1/timetables/${entryId}/restore`)),

  verify: (entryId: string) =>
    handleApiRequest(apiClient.post(`/api/v1/timetables/${entryId}/verify`)),

  checkConflicts: (data: ConflictCheckRequest) =>
    handleApiRequest<ConflictCheckResult>(apiClient.post('/api/v1/timetables/check-conflicts', data)),

  bulkCreate: (data: BulkCreateRequest) =>
    handleApiRequest(apiClient.post('/api/v1/timetables/bulk-create', data)),

  getSummary: (teacherId: string, weekStart?: string, weekCount = 1) =>
    handleApiRequest<TimetableSummary>(apiClient.get(`/api/v1/timetables/${teacherId}/summary`, {
      params: { week_start: weekStart, week_count: weekCount },
    })),

  getCalendar: (teacherId: string, weekStart?: string, weekCount = 1) =>
    handleApiRequest<CalendarView>(apiClient.get(`/api/v1/timetables/${teacherId}/calendar`, {
      params: { week_start: weekStart, week_count: weekCount },
    })),

  // Period Groups API
  getPeriodGroups: (teacherIds: string[], includeEntries = false) =>
    handleApiRequest<{ groups: PeriodGroup[] }>(apiClient.get('/api/v1/timetables/period-groups', {
      params: {
        teacher_ids: teacherIds.join(','),
        include_entries: includeEntries,
      },
    })),

  bulkUpdateValidity: (data: BulkUpdateValidityRequest) =>
    handleApiRequest<BulkUpdateValidityResponse>(apiClient.patch('/api/v1/timetables/bulk/update-validity', data)),

  revertValidity: (changeId: string) =>
    handleApiRequest<RevertValidityResponse>(apiClient.post(`/api/v1/timetables/bulk/revert-validity/${changeId}`)),
}

// Period Groups Types
export interface PeriodGroupTeacher {
  teacher_id: string
  teacher_name: string
  teacher_code?: string
  entry_count: number
  entries?: TimetableEntry[]
}

export interface PeriodGroup {
  valid_from: string | null
  valid_to: string | null
  label: string
  is_current: boolean
  teacher_count: number
  entry_count: number
  teachers: PeriodGroupTeacher[]
}

export interface TeacherValidityUpdate {
  teacher_id: string
  old_valid_from?: string | null
  old_valid_to?: string | null
  new_valid_from?: string | null
  new_valid_to?: string | null
}

export interface BulkUpdateValidityRequest {
  updates: TeacherValidityUpdate[]
  mode: 'update' | 'replace'
}

export interface BulkUpdateError {
  teacher_id: string
  error: string
}

export interface BulkUpdateValidityResponse {
  updated_count: number
  affected_teachers: number
  change_id?: string
  expires_at?: string
  errors?: BulkUpdateError[]
}

export interface RevertValidityResponse {
  reverted_count: number
  affected_teachers: number
  expired?: boolean
  errors?: BulkUpdateError[]
}
